import { fabric } from 'fabric';
import { CardType, CustomFabricObject, ProjectTypeCode } from '../../../global-types';

export const createCroppingArea = (
  currentImage: CustomFabricObject | null,
  canvas: React.MutableRefObject<fabric.Canvas>,
  croppingArea: React.MutableRefObject<fabric.Rect | undefined>,
  project_type_code: ProjectTypeCode | undefined,
  cropDeselectionHandler: () => void,
  isNewImageEditToolbarEnabled = false,
) => {
  if (!currentImage) {
    return;
  }
  const { height, width, left, top, angle, scaleX, scaleY } = currentImage;
  if (left !== undefined && top !== undefined) {
    const zoneSize = {
      width: (width as number) * (scaleX as number),
      height: (height as number) * (scaleY as number),
    };

    const croppingRect = new fabric.Rect({
      fill: '',
      originX: 'left',
      originY: 'top',
      width: zoneSize.width,
      height: zoneSize.height,
      stroke: 'black',
      strokeDashArray: [2, 2],
      visible: true,
      opacity: 1,
      left,
      top,
      angle,
      name: 'cropping-rect',
    });

    const clipPath = new fabric.Rect({
      width: zoneSize.width,
      height: zoneSize.height,
      left,
      top,
      absolutePositioned: true,
      angle,
    });

    croppingRect.clipPath = clipPath;

    // Configure the control visibility and sizing BEFORE the rect is added
    // to the canvas so the very first `drawControls` pass (triggered by
    // `canvas.add`) already reads the intended `_controlsVisibility` map.
    // In this project's fabric version the default `_controlsVisibility.mtr`
    // for a plain `fabric.Rect` is undefined, which makes
    // `Control.getVisibility()` return false — so any product code that
    // wants the styled rotate handle must set it to `true` explicitly.
    if (project_type_code === CardType.SAS && !isNewImageEditToolbarEnabled) {
      // Legacy SAS (old image edit toolbar): the crop overlay never showed
      // a rotate handle. Preserve that behaviour to avoid regressing the
      // old toolbar UI.
      croppingRect.setControlsVisibility({ mtr: false });
      croppingRect.set({ cornerSize: 28 });
    } else if (isNewImageEditToolbarEnabled) {
      // New image edit toolbar (any product code): show the styled rotate
      // handle rendered by `renderCustomCroppingControl` and match the
      // corner size so the crop handles feel consistent across products.
      croppingRect.setControlsVisibility({ mtr: true });
      croppingRect.set({ cornerSize: 28 });
    }

    currentImage.opacity = 1;
    // Make other objects unselectable/uneventable until cropping is finished, keep track of original values
    const canvasObjects = canvas.current.getObjects();
    canvasObjects.forEach((obj) => {
      if (obj.selectable === true) {
        obj.selectable = false;
        obj.data.isSelectable = true;
      }
      if (obj.evented === true) {
        obj.evented = false;
        obj.data.isEvented = true;
      }
    });

    canvas.current.add(croppingRect);
    canvas.current.setActiveObject(croppingRect);
    croppingRect.bringToFront();

    canvas.current.on('selection:cleared', cropDeselectionHandler);

    croppingArea.current = croppingRect;
  }
};
